import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';

interface ISnackState {
    value: Toast[];
}

export interface CreateToastDto {
    type: ToastType;
    message: string;
    options?: CreateToastOptions;
}

export interface Toast extends CreateToastDto {
    id: string;
    show: boolean;
    bump: boolean;
    options: CreateToastOptions & { duration: number };
}

export type CreateToastOptions = {
    actionText?: string;
    actionOnclick?: () => void;
    actionType?: ActionType;
    closeButton?: boolean;
    /** When set with closeButton, shows this text instead of an X icon. */
    dismissLabel?: string;
    /** Called when the user dismisses the toast via the close button. */
    onClose?: () => void;
    duration?: number;
    customId?: string;
};

export enum ToastType {
    SUCCESS = 'success',
    ERROR = 'error',
    INFO = 'info',
    WARNING = 'warning',
}

export enum ActionType {
    NAVIGATE = 'navigate',
    DEFAULT = 'default',
    NONE = 'none',
}

const initialState: ISnackState = {
    value: [],
};

// Counter to ensure unique IDs even when created in the same millisecond
let toastIdCounter = 0;

export const slice = createSlice({
    name: 'snackBar',
    initialState: { ...initialState },
    reducers: {
        openSnackAction: (state, action: PayloadAction<CreateToastDto>) => {
            const customId = action.payload.options?.customId;
            const id = customId || `id_${Date.now()}_${++toastIdCounter}`;
            const defaults = {
                duration: action.payload.type === ToastType.ERROR ? 5000 : 3000,
                closeButton: false,
            };
            const newToast: Toast = {
                ...action.payload,
                id,
                show: true,
                bump: false,
                options: { ...defaults, ...action.payload.options },
            };

            // Avoid mutating the state directly when pushing a new toast
            const existingToast = state.value.find(
                (toast: Toast) => toast.message === action.payload.message,
            );

            if (existingToast) {
                existingToast.bump = true; // Bump the existing toast without mutating the whole array
            } else {
                state.value.push(newToast); // Only push new toast when necessary
            }
        },
        updateToast: (
            state,
            action: PayloadAction<{ id: string; updates: Partial<Toast> }>,
        ) => {
            const { id, updates } = action.payload;
            const toast = state.value.find((t) => t.id === id);
            if (toast) {
                Object.assign(toast, updates);
            }
        },

        hideToast: (state, action: PayloadAction<string>) => {
            const toastIndex = state.value.findIndex(
                (toast) => toast.id === action.payload,
            );
            if (toastIndex !== -1) {
                state.value[toastIndex].show = false;
            }
        },
        removeToast: (state, action: PayloadAction<string>) => {
            state.value = state.value.filter(
                (toast) => toast.id !== action.payload,
            );
        },

        removeAllSnacks: (state) => {
            state.value = initialState.value;
        },
    },
});

export const {
    openSnackAction,
    removeToast,
    removeAllSnacks,
    hideToast,
    updateToast,
} = slice.actions;

export const selectSnackBars = (state: RootState) => state.snackBar.value;
export const SnackBarReducer = slice.reducer;
